<template>
  <div class="flex flex-col glass-frosted w-88.5 rounded-8xl py-4 relative max-h-70 box-border">
    <div class="flex items-center justify-between px-4">
      <div class="flex items-center gap-2">
        <button
          class="text-l-medium cursor-pointer transition-colors"
          :class="
            selectedState === 'effect'
              ? 'text-black-white'
              : 'text-text-glass-default hover:text-text-glass-hover'
          "
          @click="() => handleChangeType('effect')"
        >
          {{ $t('lighting.lightMode.lightingTitle') }}
        </button>
        <button
          v-if="hasChargingEffect"
          class="text-l-medium cursor-pointer transition-colors"
          :class="
            selectedState === 'charge'
              ? 'text-black-white'
              : 'text-text-glass-default hover:text-text-glass-hover'
          "
          @click="() => handleChangeType('charge')"
        >
          {{ $t('lighting.lightMode.chargingTitle') }}
        </button>
      </div>
      <!-- showLightingSwitch 为 false 时隐藏总开关，目前仅 P6 PLUS，原因见 useBaseLighting -->
      <Tooltip
        v-if="showLightingSwitch && currentEffect.target === 'base-light-band'"
        :content="lightingSwitchTooltip"
        side="left"
        :side-offset="-8"
      >
        <div class="flex items-center">
          <Switch
            size="lg"
            :model-value="enabledLighting"
            @update:model-value="handleLightingEnabledChange"
          />
        </div>
      </Tooltip>
    </div>
    <ScrollArea class="flex-1 overflow-x-hidden mt-3 pl-4 pr-2 mr-2 h-full">
      <Transition
        mode="out-in"
        enter-active-class="animate-in fade-in duration-200"
        leave-active-class="animate-out fade-out duration-200"
      >
        <div
          v-if="selectedState === 'effect'"
          key="effect"
          class="grid grid-cols-[repeat(auto-fill,minmax(5rem,1fr))] gap-2 pr-1"
        >
          <Tooltip
            v-for="effect in effects"
            :key="effect.id"
            :side="effect.tip ? 'bottom' : 'top'"
            :align="effect.tip ? 'start' : 'center'"
            :side-offset="effect.tip ? 4 : 0"
            :content="effect.tip ? $t('lighting.effect.battery_indicator_tip') : effect.name"
            :disabled="!effect.tip && !nameOverflowMap[effect.id]"
          >
            <Button
              class="gap-1 px-3 py-2 rounded-6xl cursor-pointer whitespace-nowrap"
              color="normal"
              :active="currentEffect.id === effect.id"
              @mouseenter="updateNameOverflow(effect.id, $event)"
              @click="() => handleChangeEffect(effect)"
            >
              <Icon :icon="effect.icon" class="size-4 shrink-0" />
              <span data-effect-name class="text-xs font-medium truncate min-w-12">
                {{ effect.name }}
              </span>
            </Button>
          </Tooltip>
        </div>
        <div v-else-if="hasChargingEffect" key="charge" class="flex flex-col gap-2">
          <div
            v-for="state in CHARGING_STATES"
            :key="state.value"
            :class="[
              'group flex items-center gap-3 px-4 py-3 h-24 rounded-4xl cursor-pointer',
              `${chargingState === state.value ? 'bg-white' : 'bg-key'}`,
            ]"
            @click="emit('update:charingState', state.value)"
          >
            <Icon
              :icon="state.icon"
              :class="[
                'size-9 shrink-0 text-black transition-colors',
                `${chargingState === state.value ? 'text-brand-blue2' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
              ]"
            />
            <div class="flex flex-col gap-2">
              <span
                :class="[
                  'text-m-medium transition-colors',
                  `${chargingState === state.value ? 'text-brand-blue2' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
                ]"
                >{{ $t(state.title) }}</span
              >
              <span
                :class="[
                  'text-s-medium transition-colors',
                  `${chargingState === state.value ? 'text-blue-dark-hover' : 'text-text-glass-default group-hover:text-text-glass-hover'}`,
                ]"
              >
                {{ $t(state.excerpt) }}
              </span>
            </div>
          </div>
        </div>
      </Transition>
    </ScrollArea>
    <Transition
      mode="out-in"
      enter-active-class="animate-in fade-in duration-200"
      leave-active-class="animate-out fade-out duration-200"
    >
      <div v-if="showSyncLighting" class="px-4 mt-2">
        <div
          class="px-2.5 py-2 flex items-center justify-between h-10 w-full rounded-full bg-neutral-light-default"
        >
          <span class="text-m-medium text-black-white">{{
            $t('lighting.lightMode.syncTitle')
          }}</span>
          <Switch
            size="lg"
            :model-value="syncLighting"
            @update:model-value="handleSyncLightingChange"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
  import { ref, computed, reactive } from 'vue'
  import { BaseChargeLightDisplayMode, LIGHTING_EFFECT_KIND } from '@rapoo/device-sdk'
  import { Button, Icon, ScrollArea, Switch, Tooltip } from '@rapoo/element'
  import { useLocaleStore } from '@/stores'
  import type { BaseLightingEffect } from '../../composables/useBaseLighting'

  type LightState = 'effect' | 'charge'

  interface ChargingState {
    icon: string
    value: BaseChargeLightDisplayMode
    title: string
    excerpt: string
  }

  const CHARGING_STATES: ChargingState[] = [
    {
      icon: 'LightEffect-Wireframe-DecorLight',
      value: 'ambient',
      title: 'lighting.lightMode.ambientLightingTitle',
      excerpt: 'lighting.lightMode.ambientLightingExcerpt',
    },
    {
      icon: 'LightEffect-Wireframe-ChargeLight',
      value: 'charging',
      title: 'lighting.lightMode.chargingLightingTitle',
      excerpt: 'lighting.lightMode.chargingLightingExcerpt',
    },
  ]

  const props = defineProps<{
    effects: BaseLightingEffect[]
    enabledLighting: boolean
    showLightingSwitch: boolean
    currentEffect: BaseLightingEffect
    chargingState: BaseChargeLightDisplayMode
    syncLighting: boolean
    showSyncLighting: boolean
  }>()

  const emit = defineEmits<{
    'update:enabledLighting': [value: boolean]
    'update:currentEffect': [value: BaseLightingEffect]
    'update:charingState': [value: BaseChargeLightDisplayMode]
    'update:syncLighting': [value: boolean]
  }>()

  const { t } = useLocaleStore()
  const selectedState = ref<LightState>('effect')
  const hasChargingEffect = computed(() =>
    props.effects.some((effect) => effect.kind === LIGHTING_EFFECT_KIND.Indicator),
  )

  /** 灯效名称是否因 truncate 溢出；仅溢出时启用名称 Tooltip（有 tip 的灯效仍始终展示说明） */
  const nameOverflowMap = reactive<Record<number, boolean>>({})
  const updateNameOverflow = (id: number, event: MouseEvent) => {
    const root = event.currentTarget as HTMLElement | null
    const nameEl = root?.querySelector<HTMLElement>('[data-effect-name]')
    nameOverflowMap[id] = !!nameEl && nameEl.scrollWidth > nameEl.clientWidth
  }

  const handleLightingEnabledChange = (value: boolean) => {
    emit('update:enabledLighting', value)
  }

  const handleSyncLightingChange = (value: boolean) => {
    emit('update:syncLighting', value)
  }

  const handleChangeType = (val: LightState) => {
    selectedState.value = val
  }

  const handleChangeEffect = (effect: BaseLightingEffect) => {
    emit('update:currentEffect', effect)
  }

  const lightingSwitchTooltip = computed(() =>
    props.enabledLighting
      ? t('lighting.switchTooltip.turnOff')
      : t('lighting.switchTooltip.turnOn'),
  )
</script>
